import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Store,
  Truck,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';

// Order status colors and labels
const STATUS_CONFIG = {
  pending: { label: 'In attesa', color: 'bg-warning text-warning-foreground', icon: Clock },
  paid: { label: 'Pagato', color: 'bg-success text-success-foreground', icon: CheckCircle },
  preparing: { label: 'In preparazione', color: 'bg-instagram-orange text-primary-foreground', icon: Clock },
  ready: { label: 'Pronto', color: 'bg-instagram-purple text-primary-foreground', icon: CheckCircle },
  completed: { label: 'Completato', color: 'bg-muted text-muted-foreground', icon: CheckCircle },
  cancelled: { label: 'Annullato', color: 'bg-destructive text-destructive-foreground', icon: XCircle }
};

// Fulfillment type config
const FULFILLMENT_CONFIG = {
  PICKUP: { label: 'Ritiro in sede', color: 'bg-success', icon: Store },
  DELIVERY: { label: 'Consegna', color: 'bg-instagram-purple', icon: Truck }
};

// Format date for display
const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/D';
  try {
    const date = parseISO(dateStr);
    return format(date, "EEEE d MMMM yyyy 'alle' HH:mm", { locale: it });
  } catch {
    return dateStr;
  }
};

// ✅ WhatsApp helpers
const normalizePhoneToWhatsApp = (phoneRaw) => {
  let digits = (phoneRaw || '').toString().replace(/\D/g, '');
  if (!digits) return '';

  if (digits.startsWith('0039')) digits = digits.slice(2);
  if (!digits.startsWith('39')) digits = `39${digits}`;

  return digits;
};

const buildWhatsAppOrderMessage = (order) => {
  const lines = [];

  const customerName = order.customer_name || '';

  lines.push(`Ciao ${customerName}!`);
  lines.push('');
  lines.push(`Il tuo ordine #${order.id} è stato ACCETTATO.`);
  lines.push('');

  if (order.fulfillment_type === 'PICKUP') {
    lines.push('Ritiro in sede');

    if (order.pickup_datetime) {
      lines.push(`Orario: ${formatDateTime(order.pickup_datetime)}`);
    } else if (order.pickup_date && order.pickup_time) {
      const iso = `${order.pickup_date}T${order.pickup_time}:00`;
      lines.push(`Orario: ${formatDateTime(iso)}`);
    } else if (order.pickup_date) {
      lines.push(`Data: ${order.pickup_date}`);
    }
  } else if (order.fulfillment_type === 'DELIVERY') {
    lines.push('Consegna a domicilio');
    if (order.delivery_address) {
      lines.push(`Indirizzo: ${order.delivery_address}`);
    }
  }

  lines.push('');
  lines.push('Dettaglio ordine:');

  const items = Array.isArray(order.items) ? order.items : [];

  items.forEach((it) => {
    const qty = it.qty ?? 1;
    const grams = it.grams ? ` (${it.grams}g)` : '';
    const name = it.product_name || it.name || 'Prodotto';
    const lineTotal = Number(it.line_total || 0).toFixed(2);

    lines.push(`- ${qty}x ${name}${grams} - €${lineTotal}`);
  });

  lines.push('');
  lines.push(`Subtotale: €${Number(order.subtotal || 0).toFixed(2)}`);

  if (Number(order.delivery_fee || 0) > 0) {
    lines.push(`Consegna: €${Number(order.delivery_fee || 0).toFixed(2)}`);
  }

  lines.push(`TOTALE: €${Number(order.total || 0).toFixed(2)}`);
  lines.push('');
  lines.push('Grazie!');

  return lines.join('\n');
};

export function OrderDetailsModal({
  orderId,
  orderData = null,
  open,
  onClose,
  onCancelSuccess,
  onDeleteSuccess,
  token,
  onAcceptSuccess,
  showManagementActions = true
}) {
  const [order, setOrder] = useState(orderData);
  const [isLoading, setIsLoading] = useState(!orderData);

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isAccepting, setIsAccepting] = useState(false);

  useEffect(() => {
    if (orderData) setOrder(orderData);
  }, [orderData]);

  useEffect(() => {
    if (!open) return;

    if (orderData) {
      setOrder(orderData);
      setIsLoading(false);
      return;
    }

    if (!orderId || !token) return;

    const fetchOrder = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `https://www.sapori1989.it/menu-digitale/api/admin/orders.php?id=${orderId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (!response.ok) throw new Error('Ordine non trovato');

        const data = await response.json();
        setOrder(data);
      } catch (error) {
        console.error('Error fetching order:', error);
        toast.error("Errore nel caricamento dell'ordine");
        onClose?.();
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [open, orderId, orderData, token, onClose]);

  useEffect(() => {
    if (!open) {
      setCancelReason('');
      setIsCancelDialogOpen(false);
      setIsCancelling(false);
      setIsDeleteDialogOpen(false);
      setIsDeleting(false);
      setIsAccepting(false);
    }
  }, [open]);

  const handleAcceptOrder = async () => {
    if (!order?.id) return;
    if (!token) {
      toast.error('Sessione scaduta, rifai login');
      return;
    }

    setIsAccepting(true);

    try {
      const response = await fetch('https://www.sapori1989.it/menu-digitale/api/orders/accept.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ order_id: order.id })
      });

      if (!response.ok) {
        let msg = 'Errore accettazione ordine';
        try {
          const err = await response.json();
          msg = err.detail || err.error || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await response.json();

      const updated = data.order || data.updated_order || null;

      let nextOrder = order;
      if (updated) {
        nextOrder = updated;
        setOrder(updated);
      } else {
        setOrder((prev) => (prev ? { ...prev, status: prev.status === 'pending' ? 'paid' : prev.status } : prev));
        nextOrder = { ...order, status: order.status === 'pending' ? 'paid' : order.status };
      }

      toast.success('Ordine accettato ✅');
      onAcceptSuccess?.(order.id);

      const waPhone = normalizePhoneToWhatsApp(nextOrder.customer_phone);
      if (waPhone) {
        const msg = buildWhatsAppOrderMessage(nextOrder);
        const waUrl = `https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`;
        window.open(waUrl, '_blank');
      } else {
        toast.error('Numero WhatsApp cliente mancante/non valido');
      }
    } catch (error) {
      console.error('Error accepting order:', error);
      toast.error(error?.message || 'Errore durante accettazione');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      toast.error('Inserisci il motivo della cancellazione');
      return;
    }
    if (!order?.id) return;
    if (!token) {
      toast.error('Sessione scaduta, rifai login');
      return;
    }

    setIsCancelling(true);

    try {
      const response = await fetch('https://www.sapori1989.it/menu-digitale/api/orders/cancel.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          order_id: order.id,
          reason: cancelReason.trim()
        })
      });

      if (!response.ok) {
        let msg = 'Errore cancellazione ordine';
        try {
          const err = await response.json();
          msg = err.detail || err.error || msg;
        } catch {}
        throw new Error(msg);
      }

      const data = await response.json();
      const updated = data.order || data.updated_order || null;

      toast.success('Ordine annullato. Cliente notificato ✅');

      if (updated) {
        setOrder(updated);
      } else {
        setOrder((prev) =>
          prev
            ? {
                ...prev,
                status: 'cancelled',
                cancelled_reason: cancelReason.trim()
              }
            : prev
        );
      }

      setIsCancelDialogOpen(false);
      onCancelSuccess?.(order.id, cancelReason.trim());
      onClose?.();
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error(error?.message || 'Errore durante la cancellazione');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!order?.id) return;
    if (!token) {
      toast.error('Sessione scaduta, rifai login');
      return;
    }

    setIsDeleting(true);

    try {
      const response = await fetch('https://www.sapori1989.it/menu-digitale/api/orders/delete.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          order_id: order.id
        })
      });

      if (!response.ok) {
        let msg = 'Errore eliminazione ordine';
        try {
          const err = await response.json();
          msg = err.detail || err.error || msg;
        } catch {}
        throw new Error(msg);
      }

      toast.success('Ordine eliminato dal calendario');
      setIsDeleteDialogOpen(false);
      onDeleteSuccess?.(order.id);
      onClose?.();
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error(error?.message || 'Errore durante eliminazione ordine');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="w-[94vw] max-w-3xl overflow-visible">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Dettaglio Ordine
              {order && (
                <Badge className={STATUS_CONFIG[order.status]?.color}>
                  {STATUS_CONFIG[order.status]?.label || order.status}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>{order && <span className="text-xs">ID: {order.id}</span>}</DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-instagram-purple" />
            </div>
          ) : order ? (
            <div className="space-y-4">
              <div className={`p-3 rounded-lg ${FULFILLMENT_CONFIG[order.fulfillment_type]?.color}/10`}>
                <div className="flex items-center gap-2">
                  {order.fulfillment_type === 'PICKUP' ? (
                    <Store className="w-5 h-5 text-success" />
                  ) : (
                    <Truck className="w-5 h-5 text-instagram-purple" />
                  )}
                  <span className="font-semibold">{FULFILLMENT_CONFIG[order.fulfillment_type]?.label}</span>
                </div>

                {order.fulfillment_type === 'PICKUP' && (order.pickup_datetime || order.pickup_date) && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    {formatDateTime(
                      order.pickup_datetime ||
                        (order.pickup_date && order.pickup_time
                          ? `${order.pickup_date}T${order.pickup_time}:00`
                          : order.pickup_date)
                    )}
                  </div>
                )}

                {order.fulfillment_type === 'DELIVERY' && order.delivery_address && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    {order.delivery_address}
                  </div>
                )}
              </div>

              <Separator />

              <div className="space-y-2">
                <h4 className="font-semibold text-foreground">Cliente</h4>
                <div className="grid gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground w-20">Nome:</span>
                    <span className="font-medium">{order.customer_name}</span>
                  </div>

                  {order.customer_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <a href={`tel:${order.customer_phone}`} className="text-instagram-purple hover:underline">
                        {order.customer_phone}
                      </a>
                    </div>
                  )}

                  {order.customer_email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <a href={`mailto:${order.customer_email}`} className="text-instagram-purple hover:underline">
                        {order.customer_email}
                      </a>
                    </div>
                  )}

                 {(order.customer_notes || order.notes) && (
                  <div className="bg-muted/50 p-2 rounded text-sm">
                 <span className="text-muted-foreground">Note: </span>
                {order.customer_notes || order.notes}
                  </div>
                )}
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <h4 className="font-semibold text-foreground">Prodotti</h4>
                <div className="space-y-2">
                  {(order.items || []).map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span>
                        {item.qty}x {item.product_name} {item.grams ? `(${item.grams}g)` : ''}
                      </span>
                      <span className="font-medium">€{Number(item.line_total || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotale</span>
                  <span>€{Number(order.subtotal || 0).toFixed(2)}</span>
                </div>

                {Number(order.delivery_fee || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Consegna</span>
                    <span>€{Number(order.delivery_fee || 0).toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between font-bold text-lg">
                  <span>Totale</span>
                  <span className="gradient-instagram-text">€{Number(order.total || 0).toFixed(2)}</span>
                </div>
              </div>

              {order.status === 'cancelled' && order.cancelled_reason && (
                <Alert variant="destructive">
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>
                    <strong>Motivo annullamento:</strong> {order.cancelled_reason}
                  </AlertDescription>
                </Alert>
              )}

              <div className="text-xs text-muted-foreground text-center">
                Ordine creato: {formatDateTime(order.created_at)}
              </div>
            </div>
          ) : null}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {showManagementActions && order?.status !== 'cancelled' && (
              <Button
                variant="default"
                onClick={handleAcceptOrder}
                disabled={isAccepting || isLoading}
                className="gradient-instagram text-primary-foreground"
              >
                {isAccepting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Accettando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Accetta ordine
                  </>
                )}
              </Button>
            )}

            {showManagementActions && order?.status !== 'cancelled' && (
              <Button variant="destructive" onClick={() => setIsCancelDialogOpen(true)}>
                <XCircle className="w-4 h-4 mr-2" />
                Annulla ordine
              </Button>
            )}

            {showManagementActions && (
              <Button variant="outline" className="border-destructive text-destructive hover:bg-destructive/10" onClick={() => setIsDeleteDialogOpen(true)}>
                <XCircle className="w-4 h-4 mr-2" />
                Elimina ordine
              </Button>
            )}

            <Button variant="outline" onClick={onClose}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Annulla Ordine
            </DialogTitle>
            <DialogDescription>
              Questa azione invierà una notifica automatica al cliente (se implementata lato backend).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>
                Stai per annullare l&apos;ordine di <strong>{order?.customer_name}</strong> per un totale di{' '}
                <strong>€{Number(order?.total || 0).toFixed(2)}</strong>.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="cancelReason">Motivo cancellazione *</Label>
              <Textarea
                id="cancelReason"
                placeholder="Es: Prodotto non disponibile, errore nel sistema..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCancelDialogOpen(false);
                setCancelReason('');
              }}
              disabled={isCancelling}
            >
              Indietro
            </Button>

            <Button
              variant="destructive"
              onClick={handleCancelOrder}
              disabled={isCancelling || !cancelReason.trim()}
            >
              {isCancelling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Annullamento...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Conferma annullamento
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Elimina Ordine
            </DialogTitle>
            <DialogDescription>
              L&apos;ordine verrà rimosso dal calendario admin e non sarà più visibile lì.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>
                Stai eliminando l&apos;ordine di <strong>{order?.customer_name}</strong> per un totale di{' '}
                <strong>â‚¬{Number(order?.total || 0).toFixed(2)}</strong>.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Indietro
            </Button>

            <Button
              variant="destructive"
              onClick={handleDeleteOrder}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Eliminazione...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Conferma eliminazione
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
